import { useRef, useState } from 'react'
import { ImagePlus, Loader2, Trash2 } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { Button, Card } from '../ui/kit'

/**
 * Das eigene Logo auf Rechnungen, Angeboten und Briefen.
 *
 * WARUM VERKLEINERT WIRD: fast jeder lädt hier das Bild hoch, das er hat — ein Foto
 * des Firmenschilds mit vier Megapixeln oder ein Export aus Canva mit 3000 Pixel
 * Kantenlänge. Auf dem Beleg ist das Logo zwei Zentimeter breit. Ein Bild, das
 * zwanzigmal größer ist als seine Darstellung, macht jede Rechnung langsam und jede
 * Mail schwer, ohne dass irgendjemand einen Unterschied sieht. Also rechnet der
 * Browser es vor dem Hochladen auf eine sinnvolle Kantenlänge herunter — beim Vermieter,
 * nicht auf unseren Servern, das kostet nichts und geht sofort.
 *
 * SVG bleibt unangetastet: es ist ohnehin klein und würde durch das Rastern schlechter.
 *
 * WARUM DER DATEINAME DEN ZEITSTEMPEL TRÄGT: die URL des Logos steht in bereits
 * versendeten Mails und in den Zwischenspeichern der CDNs. Würde ein neues Logo unter
 * demselben Namen liegen, zeigte der Beleg von gestern tagelang das alte — oder
 * schlimmer, gar keins. Ein neuer Name ist eine neue URL und wirkt sofort.
 */

/** Kantenlänge, auf die verkleinert wird. Reicht für den Druck mit 300 dpi. */
const MAX_EDGE = 600

/** Ab hier ist eine Datei so groß, dass sie nicht als Logo gemeint sein kann. */
const MAX_INPUT_BYTES = 10 * 1024 * 1024

async function shrink(file: File): Promise<Blob> {
  if (file.type === 'image/svg+xml') return file

  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
  const width = Math.max(1, Math.round(bitmap.width * scale))
  const height = Math.max(1, Math.round(bitmap.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return file
  ctx.drawImage(bitmap, 0, 0, width, height)
  bitmap.close()

  // PNG, nicht JPEG: ein Logo hat fast immer einen durchsichtigen Hintergrund, und
  // JPEG macht daraus einen schwarzen Kasten auf weißem Papier.
  return await new Promise<Blob>((resolve) => {
    canvas.toBlob((b) => resolve(b ?? file), 'image/png')
  })
}

export default function LogoCard() {
  const { lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tenant = store.db.tenant
  const logo = tenant.logo_url ?? null

  const upload = async (file: File) => {
    setError(null)
    if (file.size > MAX_INPUT_BYTES) {
      setError(de ? 'Die Datei ist zu groß (über 10 MB).' : 'That file is too large (over 10 MB).')
      return
    }
    setBusy(true)
    try {
      const blob = await shrink(file)
      const ext = file.type === 'image/svg+xml' ? 'svg' : 'png'
      const path = `${tenant.id}/logo-${Date.now()}.${ext}`

      if (!store.live || !supabase) {
        // Demomodus: das Bild bleibt im Browser. Ein Interessent soll sehen, wie sein
        // Logo auf dem Beleg wirkt, ohne dafür ein Konto anzulegen.
        const reader = new FileReader()
        const dataUrl = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(String(reader.result))
          reader.onerror = () => reject(new Error('read_failed'))
          reader.readAsDataURL(blob)
        })
        await store.saveTenant({ logo_url: dataUrl })
      } else {
        const { error: upErr } = await supabase.storage
          .from('tenant-logos')
          .upload(path, blob, { contentType: blob.type || 'image/png', upsert: true })
        if (upErr) throw new Error(upErr.message)

        const { data } = supabase.storage.from('tenant-logos').getPublicUrl(path)
        await store.saveTenant({ logo_url: data.publicUrl })

        // Das vorherige Logo aufräumen — es ist durch nichts mehr erreichbar und
        // würde sonst für immer im Speicher liegen.
        const old = logo?.split('/tenant-logos/')[1]
        if (old && old.startsWith(`${tenant.id}/`)) {
          await supabase.storage.from('tenant-logos').remove([old])
        }
      }
    } catch (e) {
      setError(String((e as Error).message))
    }
    setBusy(false)
  }

  const clear = async () => {
    setBusy(true)
    const old = logo?.split('/tenant-logos/')[1]
    await store.saveTenant({ logo_url: null })
    if (store.live && supabase && old && old.startsWith(`${tenant.id}/`)) {
      await supabase.storage.from('tenant-logos').remove([old])
    }
    setBusy(false)
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium">{de ? 'Logo' : 'Logo'}</h2>
      <p className="text-sm text-muted">
        {de
          ? 'Erscheint im Kopf von Angeboten, Rechnungen und Briefen — auch auf der Fassung, die der Gast über seinen Link öffnet.'
          : 'Appears in the header of quotes, invoices and letters — including the copy your guest opens from their link.'}
      </p>

      <div className="flex items-center gap-4">
        <div className="w-32 h-20 shrink-0 rounded-xl bg-surface-2 border border-border
                        flex items-center justify-center overflow-hidden">
          {logo
            ? <img src={logo} alt="" className="max-w-full max-h-full object-contain" />
            : <ImagePlus size={22} className="text-muted" />}
        </div>

        <div className="space-y-2 min-w-0">
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={busy}
              onClick={() => input.current?.click()}>
              {busy && <Loader2 size={15} className="animate-spin" />}
              {logo
                ? (de ? 'Ersetzen' : 'Replace')
                : (de ? 'Logo hochladen' : 'Upload a logo')}
            </Button>
            {logo && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={clear}>
                <Trash2 size={15} /> {de ? 'Entfernen' : 'Remove'}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted">
            {de
              ? 'PNG, JPEG, WebP oder SVG. Größere Bilder werden automatisch verkleinert.'
              : 'PNG, JPEG, WebP or SVG. Larger images are scaled down automatically.'}
          </p>
        </div>
      </div>

      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          // Zurücksetzen, sonst löst dieselbe Datei beim zweiten Mal kein Ereignis aus.
          e.target.value = ''
          if (file) void upload(file)
        }}
      />

      {error && <p className="text-sm text-danger">{error}</p>}
    </Card>
  )
}
